import { useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SuddenLeaveReport {
  id: string;
  teaching_date: string;
  start_date: string | null;
  end_date: string | null;
  student_id: string;
  student_full_name: string;
  student_nickname: string;
  student_grade_level: string;
  teacher_id: string;
  teacher_display: string | null;
  subject: string;
  teaching_location: string;
  sudden_leave: boolean;
  sudden_leave_admin_note: string | null;
  sudden_leave_reviewed_by: string | null;
  sudden_leave_reviewed_at: string | null;
  learning_status: string;
}

export function getBillingPeriodRange(month: number, year: number) {
  // Period: 26th of previous month to 25th of current month
  const startDate = DateTime.local(year, month, 1)
    .minus({ months: 1 })
    .set({ day: 26 });
  
  const endDate = DateTime.local(year, month, 25);
  
  return { startDate, endDate };
}

export function useSuddenLeaveReports() {
  const [reports, setReports] = useState<SuddenLeaveReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchReports = useCallback(async (month: number, year: number) => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = getBillingPeriodRange(month, year);
      const startDateStr = startDate.toISODate();
      const endDateStr = endDate.toISODate();

      const { data, error } = await supabase
        .from('student_development_reports')
        .select(`
          id,
          teaching_date,
          start_date,
          end_date,
          student_id,
          student_full_name,
          student_nickname,
          student_grade_level,
          teacher_id,
          teacher_display,
          subject,
          teaching_location,
          sudden_leave,
          sudden_leave_admin_note,
          sudden_leave_reviewed_by,
          sudden_leave_reviewed_at,
          learning_status
        `)
        .eq('learning_status', 'น้องไม่เข้าเรียน (ลากะทันหัน)')
        .gte('teaching_date', startDateStr)
        .lte('teaching_date', endDateStr)
        .order('teaching_date', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error: any) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถโหลดข้อมูลได้',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const reviewSuddenLeave = useCallback(async (
    reportId: string,
    action: 'PAY' | 'NOT_PAY',
    note: string,
    reviewerEmail: string
  ): Promise<boolean> => {
    try {
      const newSuddenLeave = action === 'NOT_PAY';
      
      const { error } = await supabase
        .from('student_development_reports')
        .update({
          sudden_leave: newSuddenLeave,
          sudden_leave_admin_note: note || null,
          sudden_leave_reviewed_by: reviewerEmail,
          sudden_leave_reviewed_at: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) throw error;

      // Update local state
      setReports(prev => prev.map(r => 
        r.id === reportId 
          ? { 
              ...r, 
              sudden_leave: newSuddenLeave, 
              sudden_leave_admin_note: note || null,
              sudden_leave_reviewed_by: reviewerEmail,
              sudden_leave_reviewed_at: new Date().toISOString(),
            }
          : r
      ));

      toast({
        title: 'บันทึกสำเร็จ',
        description: newSuddenLeave ? 'ยืนยันไม่จ่ายเงินคลาสนี้แล้ว' : 'ยืนยันจ่ายเงินคลาสนี้แล้ว',
      });

      return true;
    } catch (error: any) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message || 'ไม่สามารถบันทึกได้',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  return {
    reports,
    isLoading,
    fetchReports,
    reviewSuddenLeave,
  };
}
